import { useState } from "react";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  format?: "webp" | "auto" | "jpg" | "png";
  className?: string;
  loading?: "lazy" | "eager";
  onLoad?: () => void;
  onError?: () => void;
}

export function OptimizedImage({
  src,
  alt,
  width,
  height,
  quality = 80,
  format = "webp",
  className = "",
  loading = "lazy",
  onLoad,
  onError,
}: OptimizedImageProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Función para generar la URL optimizada de Cloudinary
  const getOptimizedUrl = (): string => {
    if (!src) return "";

    // Si ya es una URL de Cloudinary con transformaciones, la devolvemos
    if (src.includes("cloudinary.com") && src.includes("/upload/")) {
      return src;
    }

    // Si es una URL de Cloudinary sin transformaciones, agregamos las optimizaciones
    if (src.includes("cloudinary.com")) {
      const baseUrl = src.replace("/upload/", "/upload/");
      const transformations = [
        `f_${format}`,
        `q_${quality}`,
        `fl_progressive`,
        `fl_force_strip`,
      ];

      if (width) transformations.push(`w_${width}`);
      if (height) transformations.push(`h_${height}`);

      return `${baseUrl}/${transformations.join(",")}`;
    }

    // Si no es Cloudinary, devolvemos la URL original
    return src;
  };

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  const optimizedUrl = getOptimizedUrl();

  if (hasError) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-200 text-gray-500 ${className}`}
        style={{ width, height }}
      >
        <span className="text-sm">Error al cargar imagen</span>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Skeleton loader mientras carga */}
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}

      {/* Imagen principal */}
      <img
        src={optimizedUrl}
        alt={alt}
        width={width}
        height={height}
        loading={loading}
        className={`w-full h-full transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        }`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
